import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Avatar, Box, Button, Collapse, Divider, Flex, HStack, Icon, Input, Spinner, Switch, Text, Textarea, useToast, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  PiUsersThreeDuotone, PiCheckBold, PiEyeDuotone, PiLockDuotone, PiGlobeDuotone, PiUserCircleDuotone, PiEyeSlashDuotone,
  PiIdentificationCardDuotone, PiSealCheckDuotone, PiCheckCircleDuotone, PiShieldCheckDuotone, PiChatsDuotone,
  PiUserPlusDuotone, PiCalendarBlankDuotone, PiGavelDuotone, PiImageDuotone, PiTrashDuotone, PiGearDuotone, PiWarningCircleDuotone, PiFloppyDiskDuotone
} from "react-icons/pi";
import {
  getVoterGroup, updateVoterGroup, deleteVoterGroup, uploadVoterGroupAvatar, removeVoterGroupAvatar, VoterGroup
} from "@lubbezposrednio-frontend/core";
import { GLASS, drgba, EASE } from "../../components/glassUi";
import ImageCropModal from "../../components/ImageCropModal";

const MotionDiv = motion.div;
const ACCENT = "#7b2ff7";
const mediaUrl = (u?: string | null) => (u ? `${process.env.REACT_APP_BACKEND_URL}${u}` : undefined);

// ── Sekcja: ciemniejsze zaokrąglone tło + mini-nagłówek (jak w tworzeniu grupy) ──
const Section: React.FC<{ icon: React.ElementType; color: string; title: string; children: React.ReactNode }> = ({ icon, color, title, children }) => (
  <Box bg="rgba(24,12,48,0.028)" borderRadius="20px" p={{ base: "18px", md: "22px" }}>
    <HStack spacing="10px" mb="16px"><Icon as={icon} boxSize="20px" color={color} /><Text fontSize="16px" fontWeight="800" color="#241536" letterSpacing="-0.01em">{title}</Text></HStack>
    <VStack align="stretch" spacing="16px">{children}</VStack>
  </Box>
);
const SubLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fontSize="11.5px" fontWeight="800" letterSpacing="0.04em" textTransform="uppercase" color="#a29bb2" mb="10px">{children}</Text>
);

type Opt = { value: string; label: string; desc: string; icon: React.ElementType; color: string };
const OptionCard: React.FC<{ opt: Opt; active: boolean; onClick: () => void }> = ({ opt, active, onClick }) => (
  <Box as="button" type="button" onClick={onClick} textAlign="left" position="relative" p="14px 15px" borderRadius="15px"
    border="1.5px solid" borderColor={active ? opt.color : "rgba(24,12,48,0.1)"} bg={active ? drgba(opt.color, 0.08) : "rgba(255,255,255,0.5)"}
    transition={`all ${EASE}`} _hover={{ borderColor: opt.color, bg: drgba(opt.color, active ? 0.1 : 0.05), transform: "translateY(-1px)" }} _active={{ transform: "translateY(0)" }}>
    <HStack spacing="11px" align="flex-start">
      <Flex w="38px" h="38px" borderRadius="11px" align="center" justify="center" bg={active ? opt.color : drgba(opt.color, 0.12)} flexShrink={0} transition={`all ${EASE}`}>
        <Icon as={opt.icon} boxSize="20px" color={active ? "white" : opt.color} />
      </Flex>
      <Box pr="16px"><Text fontSize="14px" fontWeight="800" color="#241536" lineHeight="1.2">{opt.label}</Text><Text fontSize="12px" color="#8a8397" mt="3px" lineHeight="1.4">{opt.desc}</Text></Box>
    </HStack>
    {active && <Flex position="absolute" top="10px" right="10px" w="18px" h="18px" borderRadius="full" bg={opt.color} align="center" justify="center"><Icon as={PiCheckBold} boxSize="10px" color="white" /></Flex>}
  </Box>
);

const Segmented: React.FC<{ options: { value: string; label: string; icon: React.ElementType }[]; value: string; onChange: (v: string) => void; color: string }> = ({ options, value, onChange, color }) => (
  <Flex bg="rgba(24,12,48,0.045)" borderRadius="14px" p="5px" gap="4px">
    {options.map(o => {
      const active = o.value === value;
      return (
        <Flex key={o.value} as="button" type="button" flex="1" direction="column" align="center" gap="6px" py="11px" px="6px" borderRadius="10px"
          bg={active ? "white" : "transparent"} boxShadow={active ? "0 2px 8px -3px rgba(43,0,90,0.3)" : undefined} transition={`all ${EASE}`} onClick={() => onChange(o.value)}>
          <Icon as={o.icon} boxSize="19px" color={active ? color : "#9a94ab"} />
          <Text fontSize="12.5px" fontWeight="800" color={active ? "#241536" : "#7a748a"} textAlign="center" lineHeight="1.15">{o.label}</Text>
        </Flex>
      );
    })}
  </Flex>
);

const ToggleRow: React.FC<{ icon: React.ElementType; color: string; label: string; desc: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({ icon, color, label, desc, checked, onChange, disabled }) => (
  <Flex align="center" justify="space-between" gap="14px" opacity={disabled ? 0.6 : 1}>
    <HStack spacing="11px" minW={0}>
      <Flex w="34px" h="34px" borderRadius="10px" bg={drgba(color, 0.12)} align="center" justify="center" flexShrink={0}><Icon as={icon} boxSize="17px" color={color} /></Flex>
      <Box minW={0}><Text fontSize="14px" fontWeight="700" color="#3f3a4d">{label}</Text><Text fontSize="12.5px" color="#8a8397" lineHeight="1.4">{desc}</Text></Box>
    </HStack>
    <Switch size="lg" colorScheme="green" isChecked={checked} onChange={e => onChange(e.target.checked)} isDisabled={disabled} flexShrink={0} />
  </Flex>
);

type Props = { groupId: string; onDeleted: () => void; onSaved?: (g: VoterGroup) => void };

export const GroupSettingsPanel: React.FC<Props> = ({ groupId, onDeleted, onSaved }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [group, setGroup] = useState<VoterGroup | null>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<"private" | "public">("private");
  const [visibleOnProfile, setVisibleOnProfile] = useState(false);
  const [contentHidden, setContentHidden] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [accountRequired, setAccountRequired] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [whoCanPost, setWhoCanPost] = useState<"all" | "verified" | "owner">("all");
  const [whoCanCreateVotings, setWhoCanCreateVotings] = useState<"owner" | "moderators" | "members">("owner");
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [signupTitle, setSignupTitle] = useState("");
  const [signupDescription, setSignupDescription] = useState("");
  const [signupClosesAt, setSignupClosesAt] = useState("");

  const hydrate = (g: VoterGroup) => {
    setGroup(g);
    setName(g.name);
    setDescription(g.description || "");
    setVisibility(g.visibility === "public" ? "public" : "private");
    setVisibleOnProfile(!!g.visible_on_profile);
    setContentHidden(!!g.content_hidden);
    setRequiresApproval(!!g.requires_approval);
    setAccountRequired(!!g.account_required);
    setVerificationRequired(!!g.verification_required);
    setWhoCanPost(g.who_can_post ?? "all");
    setWhoCanCreateVotings(g.who_can_create_votings ?? "owner");
    setSignupEnabled(!!g.signup_enabled);
    setSignupTitle(g.signup_title || "");
    setSignupDescription(g.signup_description || "");
    setSignupClosesAt(g.signup_closes_at ? g.signup_closes_at.slice(0, 10) : "");
  };

  useEffect(() => {
    setLoading(true);
    getVoterGroup(groupId)
      .then(r => hydrate(r.data.voter_group))
      .catch(() => toast({ title: "Nie udało się wczytać ustawień grupy", status: "error", isClosable: true }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [groupId]);

  const titleRef = useRef<HTMLTextAreaElement>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => { const el = titleRef.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } }, [name]);
  useLayoutEffect(() => { const el = descRef.current; if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; } }, [description]);

  const accountEffective = accountRequired || verificationRequired;

  // ── Walidacja ──
  const issues: { target: string; message: string }[] = [];
  const nm = name.trim();
  if (!nm) issues.push({ target: "gs-name", message: "Podaj nazwę grupy." });
  else if (nm.length < 3) issues.push({ target: "gs-name", message: "Nazwa musi mieć min. 3 znaki." });
  if (signupEnabled && !signupTitle.trim()) issues.push({ target: "gs-signup", message: "Zapisy włączone — podaj tytuł strony zapisów." });
  const canSave = issues.length === 0;

  const save = async () => {
    if (!canSave || !group) return;
    setSaving(true);
    try {
      const r = await updateVoterGroup(group.id, {
        name: nm,
        description: description.trim(),
        visibility,
        visible_on_profile: visibleOnProfile,
        content_hidden: contentHidden,
        requires_approval: requiresApproval,
        account_required: accountEffective,
        verification_required: verificationRequired,
        who_can_post: whoCanPost,
        who_can_create_votings: whoCanCreateVotings,
        signup_enabled: signupEnabled,
        signup_title: signupEnabled ? signupTitle.trim() || null : null,
        signup_description: signupEnabled ? signupDescription.trim() || null : null,
        signup_closes_at: signupEnabled && signupClosesAt ? `${signupClosesAt}T23:59:59` : null
      });
      hydrate(r.data.voter_group);
      onSaved?.(r.data.voter_group);
      toast({ title: "Zapisano zmiany", status: "success", isClosable: true });
    } catch {
      toast({ title: "Nie udało się zapisać.", status: "error", isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!group || !window.confirm(`Usunąć grupę „${group.name}"? Tej operacji nie można cofnąć.`)) return;
    try {
      await deleteVoterGroup(group.id);
      toast({ title: "Grupa usunięta", status: "success", isClosable: true });
      onDeleted();
    } catch {
      toast({ title: "Nie udało się usunąć grupy", status: "error", isClosable: true });
    }
  };

  // ── Avatar ──
  const avatarFileRef = useRef<HTMLInputElement>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const onAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setCropFile(file);
    if (avatarFileRef.current) avatarFileRef.current.value = "";
  };
  const uploadAvatar = async (file: File) => {
    if (!group) return;
    try {
      const r = await uploadVoterGroupAvatar(group.id, file);
      hydrate(r.data.voter_group);
      onSaved?.(r.data.voter_group);
      toast({ title: "Zaktualizowano logo grupy", status: "success", isClosable: true });
    } catch {
      toast({ title: "Nie udało się wgrać logo (PNG/JPG, do 5 MB).", status: "error", isClosable: true });
    }
  };
  const removeAvatar = async () => {
    if (!group) return;
    try {
      const r = await removeVoterGroupAvatar(group.id);
      hydrate(r.data.voter_group);
      onSaved?.(r.data.voter_group);
    } catch {
      toast({ title: "Nie udało się usunąć logo", status: "error", isClosable: true });
    }
  };

  if (loading) return <Flex justify="center" py="70px"><Spinner color={ACCENT} size="lg" thickness="3px" /></Flex>;

  return (
    <>
      <MotionDiv initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
        <Box borderRadius="24px" p={{ base: "22px", md: "30px" }} position="relative" overflow="hidden" sx={GLASS}>
          <Box position="absolute" top="-90px" right="-70px" w="300px" h="300px" borderRadius="full" pointerEvents="none" sx={{ background: `radial-gradient(circle, ${drgba(ACCENT, 0.09)}, transparent 70%)` }} />

          <HStack spacing="9px" mb="18px" position="relative"><Icon as={PiGearDuotone} boxSize="18px" color={ACCENT} /><Text fontSize="12px" fontWeight="800" letterSpacing="0.05em" color="#8a8397" textTransform="uppercase">Ustawienia grupy</Text></HStack>

          {/* ── Logo + inline nazwa/opis ── */}
          <Flex gap="16px" align="flex-start" position="relative">
            <VStack spacing="8px" flexShrink={0}>
              <Avatar boxSize="72px" name={name} src={mediaUrl(group?.avatar_url)} bg={drgba(ACCENT, 0.12)} color={ACCENT} icon={<Icon as={PiUsersThreeDuotone} boxSize="30px" />} />
              <HStack spacing="6px">
                <Button size="xs" variant="ghost" leftIcon={<Icon as={PiImageDuotone} boxSize="14px" />} color={ACCENT} fontWeight="800" borderRadius="9px" onClick={() => avatarFileRef.current?.click()} _hover={{ bg: drgba(ACCENT, 0.08) }}>
                  {group?.avatar_url ? "Zmień" : "Logo"}
                </Button>
                {group?.avatar_url && (
                  <Button size="xs" variant="ghost" color="#e51f44" borderRadius="9px" onClick={removeAvatar} _hover={{ bg: "rgba(229,31,68,0.08)" }}><Icon as={PiTrashDuotone} boxSize="14px" /></Button>
                )}
              </HStack>
              <input ref={avatarFileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onAvatarChange} />
            </VStack>

            <Box flex="1" minW={0} id="gs-name">
              <Textarea ref={titleRef} value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }}
                maxLength={120} rows={1} placeholder="Nazwa grupy…" variant="unstyled" w="100%" resize="none" overflow="hidden" minH="unset"
                fontSize="clamp(24px, 3.6vw, 34px)" fontWeight="900" color="#0f0820" letterSpacing="-0.03em" lineHeight="1.14" px="8px" py="8px" mx="-8px" borderRadius="14px"
                transition={`background ${EASE}`} _placeholder={{ color: "#c8c2d6", fontWeight: 900 }} _hover={{ bg: "rgba(24,12,48,0.03)" }} _focus={{ bg: drgba(ACCENT, 0.05) }} _focusVisible={{ outline: "none", boxShadow: "none" }} sx={{ caretColor: ACCENT }} />
              <Textarea ref={descRef} value={description} onChange={e => setDescription(e.target.value)} maxLength={2000} rows={1}
                placeholder="Opis grupy (opcjonalnie)…" variant="unstyled" w="100%" resize="none" overflow="hidden" minH="unset" mt="4px"
                fontSize="clamp(14px, 1.8vw, 16px)" fontWeight="500" color="#4a4458" lineHeight="1.55" px="8px" py="6px" mx="-8px" borderRadius="12px"
                transition={`background ${EASE}`} _placeholder={{ color: "#b8b2c6", fontWeight: 500 }} _hover={{ bg: "rgba(24,12,48,0.025)" }} _focus={{ bg: drgba(ACCENT, 0.04) }} _focusVisible={{ outline: "none", boxShadow: "none" }} sx={{ caretColor: ACCENT }} />
            </Box>
          </Flex>

          {/* ── Widoczność grupy ── */}
          <Box mt={{ base: "24px", md: "30px" }}>
            <Section icon={PiEyeDuotone} color="#2563eb" title="Widoczność grupy">
              <Box>
                <SubLabel>Kto widzi grupę</SubLabel>
                <Box display="grid" gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap="10px">
                  <OptionCard active={visibility === "private"} onClick={() => setVisibility("private")} opt={{ value: "private", label: "Prywatna", desc: "Widzą tylko członkowie; grupa nie pojawia się publicznie.", icon: PiLockDuotone, color: "#6b7280" }} />
                  <OptionCard active={visibility === "public"} onClick={() => setVisibility("public")} opt={{ value: "public", label: "Publiczna", desc: "Grupa i jej ściana są widoczne publicznie.", icon: PiGlobeDuotone, color: "#2563eb" }} />
                </Box>
              </Box>
              <ToggleRow icon={PiUserCircleDuotone} color="#2563eb" label="Pokaż na moim profilu publicznym" desc="Grupa pojawi się na Twoim profilu @." checked={visibleOnProfile} onChange={setVisibleOnProfile} />
              <ToggleRow icon={PiEyeSlashDuotone} color="#7b2ff7" label="Ukryj treść ściany" desc="Posty widoczne tylko dla członków, nawet gdy grupa publiczna." checked={contentHidden} onChange={setContentHidden} />
            </Section>
          </Box>

          {/* ── Członkostwo ── */}
          <Box mt={{ base: "14px", md: "16px" }}>
            <Section icon={PiIdentificationCardDuotone} color="#10b981" title="Członkostwo">
              <ToggleRow icon={PiCheckCircleDuotone} color="#10b981" label="Dołączenie wymaga akceptacji" desc="Nowi członkowie czekają na Twoje zatwierdzenie." checked={requiresApproval} onChange={setRequiresApproval} />
              <ToggleRow icon={PiUserCircleDuotone} color="#2563eb" label="Wymagaj konta" desc="Członkiem może być tylko zalogowany użytkownik." checked={accountEffective} onChange={setAccountRequired} disabled={verificationRequired} />
              <ToggleRow icon={PiSealCheckDuotone} color="#0d9488" label="Wymagaj zweryfikowanej tożsamości" desc="mObywatel — implikuje konto." checked={verificationRequired} onChange={v => { setVerificationRequired(v); if (v) setAccountRequired(true); }} />
            </Section>
          </Box>

          {/* ── Uprawnienia ── */}
          <Box mt={{ base: "14px", md: "16px" }}>
            <Section icon={PiShieldCheckDuotone} color="#0d9488" title="Uprawnienia">
              <Box>
                <SubLabel>Kto może pisać na ścianie</SubLabel>
                <Segmented color="#0d9488" value={whoCanPost} onChange={v => setWhoCanPost(v as typeof whoCanPost)} options={[
                  { value: "all", label: "Wszyscy", icon: PiChatsDuotone },
                  { value: "verified", label: "Zweryfikowani", icon: PiSealCheckDuotone },
                  { value: "owner", label: "Właściciel", icon: PiUserCircleDuotone }
                ]} />
              </Box>
              <Box>
                <SubLabel>Kto może tworzyć głosowania</SubLabel>
                <Segmented color="#7b2ff7" value={whoCanCreateVotings} onChange={v => setWhoCanCreateVotings(v as typeof whoCanCreateVotings)} options={[
                  { value: "owner", label: "Właściciel", icon: PiUserCircleDuotone },
                  { value: "moderators", label: "Moderatorzy", icon: PiGavelDuotone },
                  { value: "members", label: "Członkowie", icon: PiUsersThreeDuotone }
                ]} />
              </Box>
            </Section>
          </Box>

          {/* ── Zapisy ── */}
          <Box mt={{ base: "14px", md: "16px" }} id="gs-signup">
            <Section icon={PiUserPlusDuotone} color="#7b2ff7" title="Publiczne zapisy">
              <ToggleRow icon={PiUserPlusDuotone} color="#7b2ff7" label="Włącz stronę zapisów" desc="Powstanie publiczny link; ludzie zapisują się do grupy (z potwierdzeniem e-mail)." checked={signupEnabled} onChange={setSignupEnabled} />
              <Collapse in={signupEnabled} animateOpacity unmountOnExit>
                <VStack align="stretch" spacing="14px" pt="2px">
                  <Box>
                    <Text fontSize="14px" fontWeight="700" color="#3f3a4d" mb="8px">Tytuł strony zapisów</Text>
                    <Input value={signupTitle} onChange={e => setSignupTitle(e.target.value)} maxLength={120} placeholder="np. Dołącz do naszej wspólnoty" h="46px" borderRadius="12px" bg="rgba(255,255,255,0.72)" border="1.5px solid rgba(24,12,48,0.1)" fontWeight="600" _hover={{ borderColor: drgba("#7b2ff7", 0.4) }} _focusVisible={{ borderColor: "#7b2ff7", boxShadow: `0 0 0 3px ${drgba("#7b2ff7", 0.16)}` }} />
                  </Box>
                  <Box>
                    <Text fontSize="14px" fontWeight="700" color="#3f3a4d" mb="8px">Opis (opcjonalnie)</Text>
                    <Textarea value={signupDescription} onChange={e => setSignupDescription(e.target.value)} maxLength={1000} placeholder="Krótko, kogo zapraszasz…" borderRadius="12px" bg="rgba(255,255,255,0.72)" border="1.5px solid rgba(24,12,48,0.1)" fontWeight="500" fontSize="14px" minH="80px" _hover={{ borderColor: drgba("#7b2ff7", 0.4) }} _focusVisible={{ borderColor: "#7b2ff7", boxShadow: `0 0 0 3px ${drgba("#7b2ff7", 0.16)}` }} />
                  </Box>
                  <Box>
                    <Text fontSize="14px" fontWeight="700" color="#3f3a4d" mb="8px">Zapisy zamykają się (opcjonalnie)</Text>
                    <HStack spacing="9px" maxW="240px" bg="rgba(255,255,255,0.72)" border="1.5px solid rgba(24,12,48,0.1)" borderRadius="12px" px="12px" h="46px" _focusWithin={{ borderColor: "#7b2ff7", boxShadow: `0 0 0 3px ${drgba("#7b2ff7", 0.16)}` }}>
                      <Icon as={PiCalendarBlankDuotone} boxSize="17px" color="#7b2ff7" />
                      <Input type="date" variant="unstyled" value={signupClosesAt} onChange={e => setSignupClosesAt(e.target.value)} fontWeight="700" fontSize="14px" />
                    </HStack>
                  </Box>
                </VStack>
              </Collapse>
            </Section>
          </Box>

          {/* ── Strefa niebezpieczna ── */}
          <Box mt={{ base: "18px", md: "20px" }} borderRadius="20px" p={{ base: "18px", md: "20px" }} bg="rgba(229,31,68,0.05)" border="1px solid rgba(229,31,68,0.18)">
            <Flex align={{ base: "flex-start", sm: "center" }} justify="space-between" gap="14px" direction={{ base: "column", sm: "row" }}>
              <HStack spacing="11px" align="flex-start">
                <Flex w="34px" h="34px" borderRadius="10px" bg="rgba(229,31,68,0.12)" align="center" justify="center" flexShrink={0}><Icon as={PiTrashDuotone} boxSize="17px" color="#e51f44" /></Flex>
                <Box><Text fontSize="14px" fontWeight="800" color="#b21232">Usuń grupę</Text><Text fontSize="12.5px" color="#a06070" lineHeight="1.4">Trwale usuwa grupę wraz ze ścianą i zapisami. Nie można cofnąć.</Text></Box>
              </HStack>
              <Button size="sm" variant="outline" borderColor="rgba(229,31,68,0.4)" color="#e51f44" borderRadius="11px" fontWeight="800" flexShrink={0} onClick={remove} _hover={{ bg: "rgba(229,31,68,0.08)" }}>Usuń grupę</Button>
            </Flex>
          </Box>
        </Box>
      </MotionDiv>

      {/* ── Sticky bar zapisu ── */}
      <Box position="sticky" bottom="16px" zIndex={20} mt="20px">
        <Flex align="center" justify="space-between" gap="12px" borderRadius="18px" px={{ base: "14px", md: "16px" }} py="12px" direction={{ base: "column-reverse", sm: "row" }} sx={{ ...GLASS, background: "rgba(255,255,255,0.74)" }} boxShadow="inset 0 1px 0 rgba(255,255,255,0.75), 0 -6px 28px -18px rgba(43,0,90,0.4), 0 18px 44px -26px rgba(43,0,90,0.5)">
          <Box minW={0}>
            {canSave ? (
              <Text fontSize="13px" color="#6b6480" fontWeight="600">Zmiany ustawień grupy.</Text>
            ) : (
              <HStack spacing="7px" align="flex-start"><Icon as={PiWarningCircleDuotone} boxSize="15px" color="#e08a00" mt="1px" flexShrink={0} /><Text fontSize="13px" color="#8a6d3b" fontWeight="700" lineHeight="1.4">{issues[0].message}</Text></HStack>
            )}
          </Box>
          <MotionDiv whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} style={{ flex: "0 0 auto", width: "100%", maxWidth: "220px" }}>
            <Button w="100%" bg="primary" color="white" borderRadius="14px" fontWeight="800" h="50px" px="30px" fontSize="15px" leftIcon={<Icon as={PiFloppyDiskDuotone} boxSize="18px" />} onClick={save} isLoading={saving} loadingText="Zapisuję…" isDisabled={!canSave} boxShadow="0 14px 30px -12px rgba(67,0,152,0.6)" _hover={{ bg: "#360079" }} _disabled={{ opacity: 0.5, cursor: "not-allowed", boxShadow: "none" }}>Zapisz zmiany</Button>
          </MotionDiv>
        </Flex>
      </Box>

      <ImageCropModal file={cropFile} isOpen={!!cropFile} onClose={() => setCropFile(null)} onCropped={f => { setCropFile(null); uploadAvatar(f); }} title="Przytnij logo grupy" />
    </>
  );
};

export default GroupSettingsPanel;
